/**
 * Acces aux ressources de l'API via react-query.
 *
 * Un seul endroit decrit les cles de cache et les invalidations : les
 * composants ne manipulent jamais d'URL.
 */
import { useCallback } from 'react';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AttribuerLot,
  AujourdhuiDTO,
  CompteursProspectionDTO,
  CreerDirigeant,
  CreerInteraction,
  CreerJetonApi,
  CreerLead,
  CreerUtilisateur,
  DirigeantDTO,
  EnrichirLead,
  EtablissementTrouveDTO,
  EtapeLot,
  ExecutionSyncDTO,
  FileAppelsDTO,
  ImportSirene,
  InteractionDTO,
  JetonApiCreeDTO,
  JetonApiDTO,
  LeadDetailDTO,
  LeadResumeDTO,
  MajLead,
  MajStatutCommercial,
  MajSuivi,
  MajUtilisateur,
  MembreAnnuaireDTO,
  MembreEquipeDTO,
  Opposition,
  PageRelancesDTO,
  RelanceDTO,
  RepartitionGeoDTO,
  ReponseMotDePasseProvisoireDTO,
  ReponsePaginee,
  RequeteRelancesProspection,
  ResultatLotDTO,
  ResultatSuppressionDTO,
  StatsDashboardDTO,
  StatutCommercialDTO,
  TacheDTO,
  UtilisateurCreeDTO,
} from '@leads/shared';
import { ErreurApi, aCodeErreur, appelApi, type ParamsRequete } from './api';
import type { ChangementOrdre } from './pipeline';

export const cles = {
  leads: (params: ParamsRequete) => ['leads', params] as const,
  lead: (id: string) => ['lead', id] as const,
  statuts: () => ['statuts'] as const,
  stats: (proprietaireId?: string) => ['dashboard', 'stats', proprietaireId ?? null] as const,
  geo: (proprietaireId?: string) => ['dashboard', 'geo', proprietaireId ?? null] as const,
  relances: (horizon: HorizonRelance, proprietaireId?: string) =>
    ['dashboard', 'relances', horizon, proprietaireId ?? null] as const,
  executions: () => ['imports', 'executions'] as const,
  jetons: () => ['jetons'] as const,
  tache: (file: string, id: string) => ['tache', file, id] as const,
  equipe: () => ['equipe'] as const,
  annuaire: () => ['annuaire'] as const,
  entreprises: (q: string, codePostal: string) => ['entreprises', q, codePostal] as const,
  prospection: () => ['prospection'] as const,
  compteursProspection: () => ['prospection', 'compteurs'] as const,
  aujourdhui: () => ['prospection', 'aujourdhui'] as const,
  relancesProspection: (requete: RequeteRelancesProspection) => ['prospection', 'relances', requete] as const,
  fileAppels: (nouveaux: number) => ['prospection', 'file-appels', nouveaux] as const,
};

export type HorizonRelance = 'retard' | 'jour' | 'semaine';

/* -------------------------------------------------------------------------- */
/*                                   Lectures                                  */
/* -------------------------------------------------------------------------- */

export function useLeads(
  params: ParamsRequete,
  options: { actif?: boolean } = {},
): UseQueryResult<ReponsePaginee<LeadResumeDTO>> {
  return useQuery({
    queryKey: cles.leads(params),
    queryFn: () => appelApi<ReponsePaginee<LeadResumeDTO>>('/api/v1/leads', { params }),
    enabled: options.actif ?? true,
    // Garde la page precedente visible pendant le chargement de la suivante.
    // `isPlaceholderData` signale ces donnees perimees : un appelant qui agit
    // sur un resultat (recherche globale) doit alors le rendre inerte.
    placeholderData: (precedent) => precedent,
  });
}

export function useLead(
  id: string | undefined,
  options: {
    /** Relue a chaque montage, meme fraiche : une fiche lue il y a moins de 30 s ne dit pas l'etat du moment (session d'appels). */
    relireAuMontage?: boolean;
  } = {},
): UseQueryResult<LeadDetailDTO> {
  return useQuery({
    queryKey: cles.lead(id ?? ''),
    queryFn: () => appelApi<LeadDetailDTO>(`/api/v1/leads/${id}`),
    enabled: Boolean(id),
    ...(options.relireAuMontage ? { refetchOnMount: 'always' as const } : {}),
  });
}

export function useStatuts(): UseQueryResult<StatutCommercialDTO[]> {
  return useQuery({
    queryKey: cles.statuts(),
    queryFn: () => appelApi<StatutCommercialDTO[]>('/api/v1/statuts'),
    // Referentiel quasi statique : inutile de le rafraichir souvent.
    staleTime: 10 * 60 * 1000,
  });
}

/*
 * Chiffres du tableau de bord. `proprietaireId` (reserve a stats.equipe)
 * restreint a un membre ; absent, l'API compte toute l'equipe, ou, sans
 * stats.equipe, les seuls leads de l'appelant.
 */

export function useStatsDashboard(proprietaireId?: string): UseQueryResult<StatsDashboardDTO> {
  return useQuery({
    queryKey: cles.stats(proprietaireId),
    queryFn: () => appelApi<StatsDashboardDTO>('/api/v1/dashboard/stats', { params: { proprietaireId } }),
  });
}

export function useRepartitionGeo(proprietaireId?: string): UseQueryResult<RepartitionGeoDTO> {
  return useQuery({
    queryKey: cles.geo(proprietaireId),
    queryFn: () => appelApi<RepartitionGeoDTO>('/api/v1/dashboard/geo', { params: { proprietaireId } }),
  });
}

export function useRelances(horizon: HorizonRelance, proprietaireId?: string): UseQueryResult<RelanceDTO[]> {
  return useQuery({
    queryKey: cles.relances(horizon, proprietaireId),
    queryFn: () =>
      appelApi<RelanceDTO[]>('/api/v1/dashboard/relances', { params: { horizon, proprietaireId } }),
  });
}

export function useExecutions(): UseQueryResult<ExecutionSyncDTO[]> {
  return useQuery({
    queryKey: cles.executions(),
    queryFn: () => appelApi<ExecutionSyncDTO[]>('/api/v1/imports/executions'),
  });
}

/**
 * Annule une recherche en cours, ou restee ouverte. La route clot l'execution
 * en « annulee », et le traitement qui tourne encore cote serveur (import
 * comme enrichissement) s'arrete avant son prochain lot. Les leads deja
 * ajoutes restent.
 */
export function useAnnulerExecution(): UseMutationResult<ExecutionSyncDTO, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (executionId: string) =>
      appelApi<ExecutionSyncDTO>(`/api/v1/imports/executions/${executionId}/annuler`, { methode: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cles.executions() });
    },
  });
}

/** Suit une tache de fond ; interroge l'API tant qu'elle n'est pas terminee. */
export function useTache(
  reference: { file: string; id: string } | null,
): UseQueryResult<TacheDTO> {
  return useQuery({
    queryKey: cles.tache(reference?.file ?? '', reference?.id ?? ''),
    queryFn: () => appelApi<TacheDTO>(`/api/v1/taches/${reference?.file}/${reference?.id}`),
    enabled: reference !== null,
    refetchInterval: (requete) => {
      const etat = requete.state.data?.etat;
      return etat === 'termine' || etat === 'echec' ? false : 2000;
    },
  });
}

/* -------------------------------------------------------------------------- */
/*                                   Ecritures                                 */
/* -------------------------------------------------------------------------- */

/**
 * Invalide tout ce qui depend de la liste des leads (listes, kanban, stats,
 * effectifs par etape) : toute ecriture sur un lead passe par ici. La promesse
 * se resout une fois les listes relues, et la fiche `leadId` s'il y en a une ;
 * le tableau de bord, les etapes et les vues de prospection se relisent en
 * arriere-plan.
 */
export function useInvalidationLeads(): (leadId?: string) => Promise<void> {
  const client = useQueryClient();
  // Identite stable : elle peut figurer dans les dependances d'un effet.
  return useCallback(
    async (leadId?: string) => {
      void client.invalidateQueries({ queryKey: ['dashboard'] });
      // Relances, accueil, badge et file d'appels dependent de chaque ecriture sur un lead.
      void client.invalidateQueries({ queryKey: cles.prospection() });
      // `nbLeads` de chaque etape (parametres du pipeline) : une ecriture peut changer l'etape d'un lead, ou le creer.
      void client.invalidateQueries({ queryKey: cles.statuts() });
      await Promise.all([
        client.invalidateQueries({ queryKey: ['leads'] }),
        leadId === undefined ? undefined : client.invalidateQueries({ queryKey: cles.lead(leadId) }),
      ]);
    },
    [client],
  );
}

/**
 * Ecriture sur un lead refusee en 404 : il a quitte le perimetre de l'appelant
 * depuis l'ouverture de l'ecran (pris par un collegue, supprime).
 */
function estLeadInaccessible(erreur: unknown): boolean {
  return erreur instanceof ErreurApi && erreur.statut === 404;
}

/**
 * Refus qui disent la fiche perimee : 404 (lead sorti du perimetre, ou element
 * deja supprime : l'API ne les distingue pas) ou 409 LEAD_OPPOSANT (opposition
 * enregistree depuis l'ouverture). La fiche et les listes sont alors relues pour
 * montrer le nouvel etat du lead ; l'ecran explique le refus. Sert aussi aux
 * demandes de script (lib/ia.ts), qui ne visent pas toujours le meme lead.
 */
export function estRefusFichePerimee(erreur: unknown): boolean {
  return estLeadInaccessible(erreur) || aCodeErreur(erreur, 'LEAD_OPPOSANT');
}

export function useRelectureSiFichePerimee(leadId: string): (erreur: Error) => void {
  const invalider = useInvalidationLeads();
  return (erreur) => {
    if (estRefusFichePerimee(erreur)) void invalider(leadId);
  };
}

export function useMajLead(
  leadId: string,
): UseMutationResult<LeadDetailDTO, Error, MajLead> {
  const client = useQueryClient();
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (corps: MajLead) =>
      appelApi<LeadDetailDTO>(`/api/v1/leads/${leadId}`, { methode: 'PATCH', corps }),
    onSuccess: (lead) => {
      client.setQueryData(cles.lead(leadId), lead);
      void invalider();
    },
    onError: relireSiPerimee,
  });
}

export function useMajSuivi(
  leadId: string,
): UseMutationResult<LeadDetailDTO, Error, MajSuivi> {
  const client = useQueryClient();
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (corps: MajSuivi) =>
      appelApi<LeadDetailDTO>(`/api/v1/leads/${leadId}/suivi`, { methode: 'PATCH', corps }),
    onSuccess: (lead) => {
      client.setQueryData(cles.lead(leadId), lead);
      void invalider();
    },
    onError: relireSiPerimee,
  });
}

export interface ChangementEtape {
  leadId: string;
  statutCode: string;
}

/**
 * Etape d'un lead changee depuis le pipeline (menu d'une carte, glisser-deposer).
 * Acceptee, elle est portee aussitot dans les listes chargees : la carte reste
 * dans sa nouvelle colonne pendant la relecture, et meme si celle-ci echoue.
 * Refusee en 404, la promesse de `mutateAsync` ne rejette qu'une fois les
 * listes relues : l'appelant y lit si le lead est encore a l'ecran.
 */
export function useChangerEtape(): UseMutationResult<LeadDetailDTO, Error, ChangementEtape> {
  const client = useQueryClient();
  const invalider = useInvalidationLeads();
  return useMutation({
    mutationFn: ({ leadId, statutCode }: ChangementEtape) =>
      appelApi<LeadDetailDTO>(`/api/v1/leads/${leadId}/suivi`, { methode: 'PATCH', corps: { statutCode } }),
    onSuccess: (lead) => {
      client.setQueryData(cles.lead(lead.id), lead);
      // Le commercial aussi : l'ecriture prend un lead du vivier pour son auteur (conception 3.2).
      client.setQueriesData<ReponsePaginee<LeadResumeDTO>>({ queryKey: ['leads'] }, (page) =>
        page && {
          ...page,
          donnees: page.donnees.map((l) => (l.id === lead.id ? { ...l, suivi: lead.suivi, proprietaire: lead.proprietaire, majLe: lead.majLe } : l)),
        },
      );
      void invalider(lead.id);
    },
    onError: (erreur, { leadId }) => (estLeadInaccessible(erreur) ? invalider(leadId) : undefined),
  });
}

export function useOpposition(
  leadId: string,
): UseMutationResult<LeadDetailDTO, Error, Opposition> {
  const client = useQueryClient();
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (corps: Opposition) =>
      appelApi<LeadDetailDTO>(`/api/v1/leads/${leadId}/opposition`, { methode: 'POST', corps }),
    onSuccess: (lead) => {
      client.setQueryData(cles.lead(leadId), lead);
      void invalider();
    },
    onError: relireSiPerimee,
  });
}

/** S'attribuer un lead du vivier ; 409 LEAD_DEJA_ATTRIBUE si un collegue l'a pris entre-temps. */
export function usePrendreLead(): UseMutationResult<LeadDetailDTO, Error, string> {
  const client = useQueryClient();
  const invalider = useInvalidationLeads();
  return useMutation({
    mutationFn: (leadId: string) =>
      appelApi<LeadDetailDTO>(`/api/v1/leads/${leadId}/prendre`, { methode: 'POST' }),
    onSuccess: (lead) => {
      client.setQueryData(cles.lead(lead.id), lead);
      void invalider();
    },
  });
}

export function useCreerInteraction(
  leadId: string,
): UseMutationResult<InteractionDTO, Error, CreerInteraction> {
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (corps: CreerInteraction) =>
      appelApi<InteractionDTO>(`/api/v1/leads/${leadId}/interactions`, { methode: 'POST', corps }),
    onSuccess: () => {
      void invalider(leadId);
    },
    onError: relireSiPerimee,
  });
}

/**
 * Suppression d'un echange. La promesse de `mutateAsync` ne se resout qu'une
 * fois la fiche relue : la confirmation se ferme sur un historique a jour.
 */
export function useSupprimerInteraction(
  leadId: string,
): UseMutationResult<void, Error, string> {
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (interactionId: string) =>
      appelApi<void>(`/api/v1/leads/${leadId}/interactions/${interactionId}`, {
        methode: 'DELETE',
      }),
    onSuccess: () => invalider(leadId),
    onError: relireSiPerimee,
  });
}

/**
 * Suppression definitive de leads, unitaire ou par lot.
 *
 * `inclureOpposants` doit etre demande explicitement : l'API refuse sinon de
 * supprimer une fiche en opposition RGPD, dont la disparition effacerait la
 * trace du refus de contact.
 *
 * Comme les autres actions groupees, les listes sont relues meme en echec : une
 * reponse perdue en route ne dit pas ce que la base a garde.
 */
export function useSupprimerLeads(): UseMutationResult<
  ResultatSuppressionDTO,
  Error,
  { ids: string[]; inclureOpposants?: boolean }
> {
  const invalider = useInvalidationLeads();
  return useMutation({
    mutationFn: ({ ids, inclureOpposants = false }) =>
      appelApi<ResultatSuppressionDTO>('/api/v1/leads/suppression', {
        methode: 'POST',
        corps: { ids, inclureOpposants },
      }),
    onSettled: () => {
      void invalider();
    },
  });
}

export function useCreerDirigeant(
  leadId: string,
): UseMutationResult<DirigeantDTO, Error, CreerDirigeant> {
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (corps: CreerDirigeant) =>
      appelApi<DirigeantDTO>(`/api/v1/leads/${leadId}/dirigeants`, { methode: 'POST', corps }),
    onSuccess: () => {
      void invalider(leadId);
    },
    onError: relireSiPerimee,
  });
}

/** Comme `useSupprimerInteraction` : resolue une fois la fiche relue. */
export function useSupprimerDirigeant(
  leadId: string,
): UseMutationResult<void, Error, string> {
  const invalider = useInvalidationLeads();
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (dirigeantId: string) =>
      appelApi<void>(`/api/v1/leads/${leadId}/dirigeants/${dirigeantId}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(leadId),
    onError: relireSiPerimee,
  });
}

export function useEnrichirLead(
  leadId: string,
): UseMutationResult<TacheDTO, Error, EnrichirLead> {
  const relireSiPerimee = useRelectureSiFichePerimee(leadId);
  return useMutation({
    mutationFn: (corps: EnrichirLead) =>
      appelApi<TacheDTO>(`/api/v1/leads/${leadId}/enrichir`, { methode: 'POST', corps }),
    onError: relireSiPerimee,
  });
}

export function useImportSirene(): UseMutationResult<TacheDTO, Error, ImportSirene> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: ImportSirene) =>
      appelApi<TacheDTO>('/api/v1/imports/sirene', { methode: 'POST', corps }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cles.executions() });
    },
  });
}

/* ------------------------------- Statuts ---------------------------------- */

/** Relit les etapes et ce qui en depend ; la promesse se resout une fois les donnees relues. */
function useInvalidationStatuts(): () => Promise<unknown> {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: cles.statuts() }),
      client.invalidateQueries({ queryKey: ['leads'] }),
      client.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
}

export function useCreerStatut(): UseMutationResult<
  StatutCommercialDTO,
  Error,
  MajStatutCommercial
> {
  const invalider = useInvalidationStatuts();
  return useMutation({
    mutationFn: (corps: MajStatutCommercial) =>
      appelApi<StatutCommercialDTO>('/api/v1/statuts', { methode: 'POST', corps }),
    onSuccess: () => {
      void invalider();
    },
  });
}

export function useMajStatut(): UseMutationResult<
  StatutCommercialDTO,
  Error,
  { id: string; corps: Partial<MajStatutCommercial> }
> {
  const invalider = useInvalidationStatuts();
  return useMutation({
    mutationFn: ({ id, corps }: { id: string; corps: Partial<MajStatutCommercial> }) =>
      appelApi<StatutCommercialDTO>(`/api/v1/statuts/${id}`, { methode: 'PATCH', corps }),
    onSuccess: () => {
      void invalider();
    },
  });
}

export function useSupprimerStatut(): UseMutationResult<
  void,
  Error,
  { id: string; remplacantCode?: string }
> {
  const invalider = useInvalidationStatuts();
  return useMutation({
    mutationFn: ({ id, remplacantCode }: { id: string; remplacantCode?: string }) =>
      appelApi<void>(`/api/v1/statuts/${id}`, {
        methode: 'DELETE',
        params: remplacantCode ? { remplacantCode } : undefined,
      }),
    onSuccess: () => {
      void invalider();
    },
  });
}

/**
 * Applique un reordonnancement : une requete par etape deplacee, en sequence,
 * pour que l'ordre final ne depende pas de l'ordre d'arrivee des reponses.
 */
export function useReordonnerStatuts(): UseMutationResult<void, Error, ChangementOrdre[]> {
  const invalider = useInvalidationStatuts();
  return useMutation({
    mutationFn: async (changements: ChangementOrdre[]) => {
      for (const changement of changements) {
        await appelApi<StatutCommercialDTO>(`/api/v1/statuts/${changement.id}`, {
          methode: 'PATCH',
          corps: { ordre: changement.ordre },
        });
      }
    },
    // Meme apres un echec partiel, la liste affichee doit refleter la base. La
    // mutation ne s'acheve qu'une fois l'ordre relu : l'ecran place alors le
    // focus sur l'ordre final et ne calcule pas un deplacement sur l'ancien.
    onSettled: invalider,
  });
}

/* ------------------------------ Jetons d'API ------------------------------ */

export function useJetons(): UseQueryResult<JetonApiDTO[]> {
  return useQuery({
    queryKey: cles.jetons(),
    queryFn: () => appelApi<JetonApiDTO[]>('/api/v1/jetons'),
  });
}

/**
 * Creation d'un jeton.
 *
 * Le secret n'est renvoye qu'ici et n'est jamais remis en cache : il vit dans
 * l'etat local de l'ecran, le temps que l'utilisateur le copie.
 */
export function useCreerJeton(): UseMutationResult<JetonApiCreeDTO, Error, CreerJetonApi> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: CreerJetonApi) =>
      appelApi<JetonApiCreeDTO>('/api/v1/jetons', { methode: 'POST', corps }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cles.jetons() });
    },
  });
}

export function useRevoquerJeton(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => appelApi<void>(`/api/v1/jetons/${id}`, { methode: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cles.jetons() });
    },
  });
}

/* -------------------------------- Equipe ---------------------------------- */

export function useEquipe(): UseQueryResult<MembreEquipeDTO[]> {
  return useQuery({
    queryKey: cles.equipe(),
    queryFn: () => appelApi<MembreEquipeDTO[]>('/api/v1/utilisateurs'),
  });
}

/** Comptes actifs : listes d'attribution, filtres par commercial, perimetre des chiffres. */
export function useAnnuaire(options: { actif?: boolean } = {}): UseQueryResult<MembreAnnuaireDTO[]> {
  return useQuery({
    queryKey: cles.annuaire(),
    queryFn: () => appelApi<MembreAnnuaireDTO[]>('/api/v1/utilisateurs/annuaire'),
    enabled: options.actif ?? true,
    // Les comptes changent rarement : inutile de les relire a chaque ecran.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * L'equipe et l'annuaire (listes d'attribution, filtres) decrivent les memes
 * comptes : toute modification d'un compte, son propre nom compris, les fait
 * relire tous deux.
 */
export function useInvalidationEquipe(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: cles.equipe() });
    void client.invalidateQueries({ queryKey: cles.annuaire() });
  };
}

export function useMajMembre(): UseMutationResult<
  MembreEquipeDTO,
  Error,
  { id: string; corps: MajUtilisateur }
> {
  const invalider = useInvalidationEquipe();
  return useMutation({
    mutationFn: ({ id, corps }: { id: string; corps: MajUtilisateur }) =>
      appelApi<MembreEquipeDTO>(`/api/v1/utilisateurs/${id}`, { methode: 'PATCH', corps }),
    // Meme refusee (dernier administrateur...), la table doit refleter la base.
    onSettled: invalider,
  });
}

/*
 * Un mot de passe provisoire n'existe en clair que dans la reponse : gcTime 0
 * retire la mutation du cache des que la fenetre qui l'affiche disparait.
 */

export function useCreerMembre(): UseMutationResult<UtilisateurCreeDTO, Error, CreerUtilisateur> {
  const invalider = useInvalidationEquipe();
  return useMutation({
    mutationFn: (corps: CreerUtilisateur) =>
      appelApi<UtilisateurCreeDTO>('/api/v1/utilisateurs', { methode: 'POST', corps }),
    onSuccess: invalider,
    gcTime: 0,
  });
}

export function useReinitialiserMotDePasse(): UseMutationResult<ReponseMotDePasseProvisoireDTO, Error, string> {
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<ReponseMotDePasseProvisoireDTO>(`/api/v1/utilisateurs/${id}/reinitialiser-mot-de-passe`, {
        methode: 'POST',
      }),
    gcTime: 0,
  });
}

/* ------------------------- Creation et actions groupees ------------------------- */

/** Recherche dans le registre avant une creation ; `null` : rien a chercher. */
export function useRechercheEntreprises(recherche: { q: string; codePostal: string } | null): UseQueryResult<EtablissementTrouveDTO[]> {
  return useQuery({
    queryKey: cles.entreprises(recherche?.q ?? '', recherche?.codePostal ?? ''),
    queryFn: () => appelApi<EtablissementTrouveDTO[]>('/api/v1/entreprises/recherche', {
      params: { q: recherche?.q, codePostal: recherche?.codePostal || undefined },
    }),
    enabled: recherche !== null,
    // Le debit de l'INSEE est compte : une recherche deja faite se relit dans le cache.
    staleTime: 60_000,
    // Un 502 du registre s'explique a l'ecran ; reessayer ne ferait que le retarder.
    retry: false,
  });
}

/** Creation manuelle : la fiche creee est mise en cache pour s'ouvrir aussitot. */
export function useCreerLead(): UseMutationResult<LeadDetailDTO, Error, CreerLead> {
  const client = useQueryClient();
  const invalider = useInvalidationLeads();
  return useMutation({
    mutationFn: (corps: CreerLead) => appelApi<LeadDetailDTO>('/api/v1/leads', { methode: 'POST', corps }),
    onSuccess: (lead) => {
      client.setQueryData(cles.lead(lead.id), lead);
      void invalider();
      // `dejaEnBase` a change pour ce SIRET.
      void client.invalidateQueries({ queryKey: ['entreprises'] });
    },
  });
}

/* Actions groupees : listes relues meme en echec, comme pour la suppression (`useSupprimerLeads`). */

export function useAttribuerLot(): UseMutationResult<ResultatLotDTO, Error, AttribuerLot> {
  const invalider = useInvalidationLeads();
  return useMutation({
    mutationFn: (corps: AttribuerLot) => appelApi<ResultatLotDTO>('/api/v1/leads/lot/attribuer', { methode: 'POST', corps }),
    onSettled: () => {
      void invalider();
    },
  });
}

export function useChangerEtapeLot(): UseMutationResult<ResultatLotDTO, Error, EtapeLot> {
  const invalider = useInvalidationLeads();
  return useMutation({
    mutationFn: (corps: EtapeLot) => appelApi<ResultatLotDTO>('/api/v1/leads/lot/etape', { methode: 'POST', corps }),
    // Relit aussi les etapes : une etape refusee a pu etre supprimee depuis l'ouverture.
    onSettled: () => {
      void invalider();
    },
  });
}

/* ------------------------------ Prospection ------------------------------- */

/** Badge « Relances » : relu chaque minute (conception 7.5). */
export const INTERVALLE_COMPTEURS_MS = 60_000;

export function useCompteursProspection(): UseQueryResult<CompteursProspectionDTO> {
  return useQuery({
    queryKey: cles.compteursProspection(),
    queryFn: () => appelApi<CompteursProspectionDTO>('/api/v1/prospection/compteurs'),
    refetchInterval: INTERVALLE_COMPTEURS_MS,
    // Onglet cache : aucune requete ; au retour sur l'onglet, le compteur est relu s'il date.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/*
 * L'accueil et les relances sont relus au retour sur l'onglet, ce que le client
 * de l'application ne fait pas (app/App.tsx) : le badge, relu chaque minute,
 * diverge sinon de la liste, et un lead passe en opposition reste « appelable »
 * dans une liste ouverte depuis des heures. Des donnees de moins de 30 s
 * (staleTime du client) ne sont pas relues.
 */

export function useAujourdhui(): UseQueryResult<AujourdhuiDTO> {
  return useQuery({
    queryKey: cles.aujourdhui(),
    queryFn: () => appelApi<AujourdhuiDTO>('/api/v1/prospection/aujourdhui'),
    refetchOnWindowFocus: true,
  });
}

export function useRelancesProspection(
  requete: RequeteRelancesProspection,
  options: { actif?: boolean } = {},
): UseQueryResult<PageRelancesDTO> {
  return useQuery({
    queryKey: cles.relancesProspection(requete),
    queryFn: () => appelApi<PageRelancesDTO>('/api/v1/prospection/relances', { params: { ...requete } }),
    enabled: options.actif ?? true,
    // Garde l'onglet precedent (et ses compteurs) affiche pendant le chargement du suivant.
    placeholderData: (precedent) => precedent,
    refetchOnWindowFocus: true,
  });
}

function optionsFileAppels(nouveaux: number) {
  return queryOptions({
    queryKey: cles.fileAppels(nouveaux),
    queryFn: () => appelApi<FileAppelsDTO>('/api/v1/prospection/file-appels', { params: { nouveaux } }),
    // Elle change a chaque appel consigne : relue a chaque ouverture de l'ecran de depart.
    staleTime: 0,
  });
}

export function useFileAppels(nouveaux: number, options: { actif?: boolean } = {}): UseQueryResult<FileAppelsDTO> {
  return useQuery({ ...optionsFileAppels(nouveaux), enabled: options.actif ?? true });
}

/**
 * Relecture au clic de « Demarrer » : l'ecran de depart a pu rester ouvert des heures, et la session se fige sur la
 * file du moment (leads appeles ailleurs depuis, ordre, scripts). Meme cle que `useFileAppels`, qui en lit la reponse.
 */
export function useRelectureFileAppels(nouveaux: number): () => Promise<FileAppelsDTO> {
  const client = useQueryClient();
  return useCallback(() => client.fetchQuery(optionsFileAppels(nouveaux)), [client, nouveaux]);
}
